import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { usePantryName } from './usePantryName';
import { InventoryItem, updateItemAndSync, markAsEatenAndSync } from '../../redux/inventorySlice';
import { learnZone, savePantryLearning } from '../../redux/pantryLearningSlice';
import { primaryKey } from '../../utils/pantryDuplicates';
import { PantryLocation } from '../../utils/pantryLocation';
import { daysUntilExpiry, estimateExpiry } from '../../services/pantryFreshnessService';
import { trackEvent } from '../../services/analyticsService';
import { RootState, AppDispatch } from '../../redux/store';

/**
 * "Still good" re-dates the item from what it is, then adds a week of grace.
 *
 * It used to add a flat seven days to everything, which is the same
 * category-blind guess this whole service replaced — confirming a whole chicken
 * and confirming a cabbage bought you the same week.
 */
export function buildStillGoodUpdates(
  item?: Pick<InventoryItem, 'name' | 'category' | 'location' | 'opened'>,
  today: Date = new Date()
): Partial<InventoryItem> {
  const fresh = item
    ? estimateExpiry(item.category, item.location || 'fridge', today, item.name, item.opened)
    : undefined;
  const days = fresh ? Math.max(7, daysUntilExpiry({ expiryDate: fresh }, today) ?? 7) : 7;
  const d = new Date(today);
  d.setDate(d.getDate() + days);
  return {
    status: 'active',
    expiryDate: {
      day: d.getDate(),
      month: d.getMonth() + 1,
      year: d.getFullYear(),
      confidence: 75,
      explanation: 'Confirmed by user',
      source: 'user',
    },
  };
}

export function buildPlusThreeUpdates(item: InventoryItem, today: Date = new Date()): Partial<InventoryItem> {
  const base = daysUntilExpiry(item, today) ?? 3;
  const d = new Date(today);
  d.setDate(d.getDate() + base + 3);
  return {
    expiryDate: {
      day: d.getDate(),
      month: d.getMonth() + 1,
      year: d.getFullYear(),
      confidence: 75,
      explanation: 'Extended by user (+3 days)',
      source: 'user',
    },
  };
}

export function buildKeepFiveUpdates(today: Date = new Date()): Partial<InventoryItem> {
  const d = new Date(today);
  d.setDate(d.getDate() + 5);
  return {
    expiryDate: {
      day: d.getDate(),
      month: d.getMonth() + 1,
      year: d.getFullYear(),
      confidence: 75,
      explanation: 'Confirmed in flash review',
      source: 'user',
    },
  };
}

/**
 * Moving food re-dates it, in both directions.
 *
 * This used to re-date only on the way *out* of the freezer or the pantry,
 * which left the one move that matters most doing nothing at all: shrimp put in
 * the freezer kept its two-day fridge date instead of the table's 180
 * (`shelfLife.ts` stores days per zone). `freshnessOf` hides that — a freezer
 * row always reads "fresh" — so the number went quietly negative underneath and
 * the pill ended up saying "Fresh · ~-2 days".
 *
 * Shelf life was always a function of (food, zone, opened); the asymmetry was
 * the bug, not the rule. This now reads like `buildOpenedUpdates` below:
 * re-estimate from today, unless the cook set the date by hand.
 *
 * A sideways move (fridge -> counter and back) therefore restarts the clock,
 * which is generous — but the app already offers "Still good" and "+3 days" for
 * exactly that, and no date at all is far worse than a slightly kind one.
 */
export function buildMoveUpdates(
  item: InventoryItem,
  zone: PantryLocation,
  today: Date = new Date()
): Partial<InventoryItem> {
  // A date the cook set by hand outranks anything the estimator would say, so
  // moving a corrected item between zones no longer silently undoes them.
  if (item.expiryDate?.source === 'user') return { location: zone };

  // No estimate for the target zone (a frozen item put on the shelf) leaves the
  // old date alone. Harmless: `freshnessOf` answers "stable" for the pantry and
  // the label never prints the number.
  const expiry = estimateExpiry(item.category, zone, today, item.name, item.opened);
  return expiry ? { location: zone, expiryDate: expiry } : { location: zone };
}

/**
 * Marking an item opened re-dates it — that is the whole reason to track it.
 * A correction the cook already made still wins.
 */
export function buildOpenedUpdates(
  item: InventoryItem,
  opened: boolean,
  today: Date = new Date()
): Partial<InventoryItem> {
  const base: Partial<InventoryItem> = opened
    ? { opened: true, openedAt: today.toISOString() }
    : { opened: false };
  if (item.expiryDate?.source === 'user') return base;
  const expiry = estimateExpiry(item.category, item.location || 'fridge', today, item.name, opened);
  return expiry ? { ...base, expiryDate: expiry } : base;
}

/**
 * What a correction taught us, reported once per correction.
 *
 * The table is checked in precisely so a wrong row can be found and fixed; this
 * event is how you find it. `matchedKey` is the row to go and edit.
 */
export function correctionProps(item: InventoryItem, correctedDays: number, today: Date = new Date()) {
  return {
    name: item.name,
    category: item.category,
    zone: item.location,
    opened: !!item.opened,
    estimatedDays: daysUntilExpiry(item, today),
    correctedDays,
    estimateSource: item.expiryDate?.source ?? 'unknown',
    matchedKey: item.expiryDate?.matchedKey ?? null,
  };
}

export interface PantryActions {
  stillGood(item: InventoryItem): void;
  plusThreeDays(item: InventoryItem): void;
  setOpened(item: InventoryItem, opened: boolean): void;
  markEaten(item: InventoryItem): void;
  markTossed(item: InventoryItem): void;
  moveTo(item: InventoryItem, zone: PantryLocation): void;
}

export function usePantryActions(flash: (message: string, undo?: () => void) => void): PantryActions {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const pantryName = usePantryName();
  const userId = useSelector((state: RootState) => state.auth?.user?.uid);
  const householdId = useSelector((state: RootState) => state.auth?.householdId);

  return {
    stillGood(item: InventoryItem) {
      const updates = buildStillGoodUpdates(item);
      dispatch(updateItemAndSync({ id: item.id, updates, userId }));
      trackEvent('pantry_item_action', { action: 'still_good', zone: item.location });
      trackEvent('pantry_expiry_corrected', {
        action: 'still_good',
        ...correctionProps(item, daysUntilExpiry({ expiryDate: updates.expiryDate }) ?? 7),
      });
      flash(t('pantry.item.freshnessUpdated'));
    },

    plusThreeDays(item: InventoryItem) {
      const updates = buildPlusThreeUpdates(item);
      dispatch(updateItemAndSync({ id: item.id, updates, userId }));
      trackEvent('pantry_item_action', { action: 'plus_three', zone: item.location });
      trackEvent('pantry_expiry_corrected', {
        action: 'plus_three',
        ...correctionProps(item, daysUntilExpiry({ expiryDate: updates.expiryDate }) ?? 3),
      });
      flash(t('pantry.item.freshnessExtended'));
    },

    setOpened(item: InventoryItem, opened: boolean) {
      const updates = buildOpenedUpdates(item, opened);
      dispatch(updateItemAndSync({ id: item.id, updates, userId }));
      trackEvent('pantry_item_action', { action: opened ? 'opened' : 'resealed', zone: item.location });
      flash(t(opened ? 'pantry.item.markedOpened' : 'pantry.item.markedSealed'));
    },

    markEaten(item: InventoryItem) {
      dispatch(markAsEatenAndSync({ itemId: item.id, userId }));
      trackEvent('pantry_item_action', { action: 'eaten', zone: item.location });
      flash(t('pantry.item.eatenToast', { name: pantryName(item) }), () => {
        dispatch(updateItemAndSync({ id: item.id, updates: { status: 'active' }, userId }));
      });
    },

    markTossed(item: InventoryItem) {
      const updates = { status: 'discarded' as const, consumedBeforeExpiry: false };
      dispatch(updateItemAndSync({ id: item.id, updates, userId }));
      trackEvent('pantry_item_action', { action: 'tossed', zone: item.location });
      // Thrown out while the pill still said "fresh" is the loudest possible
      // signal that a table row is too generous.
      trackEvent('pantry_expiry_corrected', { action: 'tossed', ...correctionProps(item, 0) });
      flash(t('pantry.item.tossedToast', { name: pantryName(item) }), () => {
        dispatch(updateItemAndSync({ id: item.id, updates: { status: 'active' }, userId }));
      });
    },

    moveTo(item: InventoryItem, zone: PantryLocation) {
      const updates = buildMoveUpdates(item, zone);
      // Moving a row also answers "where does this live", so the row stops
      // asking — see needsSorting on InventoryItem.
      dispatch(updateItemAndSync({ id: item.id, updates: { ...updates, needsSorting: false }, userId }));

      // The user physically knows where they keep this. That beats any keyword
      // rule, and it should hold for the next jar of the same thing too.
      dispatch(learnZone({ key: primaryKey(item.name), zone }));
      dispatch(savePantryLearning({ userId, householdId }));

      trackEvent('pantry_item_action', { action: 'moved', zone });
      const zoneName = t(`pantry.zones.${zone}`);
      flash(t('pantry.item.movedTo', { name: pantryName(item), zone: zoneName }));
    },
  };
}
